import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, LinearProgress,
  IconButton, Tooltip, List, ListItem, ListItemIcon, ListItemText,
  Checkbox, Divider, InputAdornment,
} from '@mui/material';
import {
  FlightTakeoff, Add, CheckCircle, RadioButtonUnchecked,
  Search, Verified, ContentCopy, OpenInNew,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  not_started: { label: 'Not Started', color: '#78909c', bg: '#f5f5f5' },
  in_progress: { label: 'In Progress', color: '#1565c0', bg: '#e3f2fd' },
  submitted:   { label: 'Submitted',   color: '#e65100', bg: '#fff3e0' },
  verified:    { label: 'Verified ✓',  color: '#2e7d32', bg: '#e8f5e9' },
};

const TASK_TYPE_ICON: Record<string, string> = {
  personal_info:     '👤',
  bank_details:      '🏦',
  emergency_contact: '🚨',
  document_upload:   '📄',
  policy_sign_off:   '✍️',
  equipment_pref:    '💻',
  it_setup:          '🔧',
  welcome_survey:    '👋',
};

interface PBRecord {
  id: string; employeeId: string; status: string; startDate?: string;
  deadline?: string; completedTasks: number; totalTasks: number;
  accessToken?: string; welcomeMessage?: string;
  employee?: { firstName: string; lastName: string; email?: string; department?: { name: string }; position?: { title: string } };
  tasks?: any[];
  verifiedAt?: string; hrNotes?: string;
  personalEmail?: string; phoneNumber?: string; address?: string;
  bankName?: string; bankCode?: string; bankAccount?: string; bankAccountName?: string; bankBranch?: string;
  emergencyName?: string; emergencyRelationship?: string; emergencyPhone?: string;
  laptopOs?: string; dietaryRequirements?: string;
}

interface Stats { notStarted: number; inProgress: number; submitted: number; verified: number; overdueCount: number; total: number }

export default function PreBoarding() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');

  const [tab, setTab] = useState(0);
  const [records, setRecords] = useState<PBRecord[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState<PBRecord | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ employeeId: '', startDate: '', deadline: '', welcomeMessage: '' });
  const [saving, setSaving] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState<string | null>(null);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [copied, setCopied] = useState(false);

  const STATUS_TABS = ['All', 'Not Started', 'In Progress', 'Submitted', 'Verified'];
  const statusMap: Record<number, string | undefined> = { 0: undefined, 1: 'not_started', 2: 'in_progress', 3: 'submitted', 4: 'verified' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusMap[tab]) params.set('status', statusMap[tab]!);
      const calls: Promise<any>[] = [
        apiFetch(`/api/pre-boarding?${params}`),
        apiFetch('/api/pre-boarding/stats'),
        apiFetch('/api/employees?limit=300'),
      ];
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setRecords(results[0].value ?? []);
      if (results[1].status === 'fulfilled') setStats(results[1].value);
      if (results[2].status === 'fulfilled') {
        const d = (results[2] as any).value;
        setEmployees(Array.isArray(d) ? d : (d.data ?? []));
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { if (isHR) load(); else setLoading(false); }, [load, isHR]);

  const create = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/pre-boarding', { method: 'POST', body: JSON.stringify(createForm) });
      setCreateOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const verify = async () => {
    if (!verifyOpen) return;
    await apiFetch(`/api/pre-boarding/${verifyOpen}/verify`, { method: 'PATCH', body: JSON.stringify({ hrNotes: verifyNotes }) });
    setVerifyOpen(null);
    load();
    if (selected?.id === verifyOpen) {
      const fresh = await apiFetch(`/api/pre-boarding/${verifyOpen}`);
      setSelected(fresh);
    }
  };

  const toggleTask = async (recordId: string, taskId: string) => {
    await apiFetch(`/api/pre-boarding/${recordId}/tasks/${taskId}/toggle`, { method: 'PATCH' });
    const fresh = await apiFetch(`/api/pre-boarding/${recordId}`);
    setSelected(fresh);
    load();
  };

  const selectRecord = async (r: PBRecord) => {
    const full = await apiFetch(`/api/pre-boarding/${r.id}`);
    setSelected(full);
  };

  const copyPortalLink = (token: string) => {
    const url = `${window.location.origin}/pre-boarding/portal/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filtered = records.filter(r => {
    if (!search) return true;
    const name = `${r.employee?.firstName} ${r.employee?.lastName}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  if (!isHR) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <FlightTakeoff sx={{ fontSize: 64, color: '#C08A2E', mb: 2 }} />
        <Typography variant="h5">Pre-Boarding Portal</Typography>
        <Typography color="text.secondary">Use the personalised link sent to you by HR to complete your pre-boarding.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
      {/* Left */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#C08A2E', width: 44, height: 44 }}><FlightTakeoff /></Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>Pre-Boarding</Typography>
              <Typography variant="body2" color="text.secondary">New hire self-service before day 1</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#9E6F22' } }}>
            Add New Hire
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Not Started',  value: stats.notStarted,  color: '#78909c' },
              { label: 'In Progress',  value: stats.inProgress,  color: '#1565c0' },
              { label: 'Submitted',    value: stats.submitted,   color: '#e65100' },
              { label: 'Verified',     value: stats.verified,    color: '#2e7d32' },
              { label: 'Overdue',      value: stats.overdueCount,color: '#b71c1c' },
            ].map(s => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={s.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {(stats?.overdueCount ?? 0) > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{stats!.overdueCount}</strong> new hire{stats!.overdueCount !== 1 ? 's' : ''} past their pre-boarding deadline without submitting.
          </Alert>
        )}

        <Paper sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 2 }}>
          <TextField size="small" placeholder="Search new hire…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </Paper>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
          {STATUS_TABS.map((t, i) => <Tab key={i} label={t} />)}
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>New Hire</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Deadline</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Portal Link</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No records found</TableCell></TableRow>
                )}
                {filtered.map(r => {
                  const pct = r.totalTasks > 0 ? Math.round((r.completedTasks / r.totalTasks) * 100) : 0;
                  const isOverdue = r.deadline && new Date(r.deadline) < new Date() && !['submitted', 'verified'].includes(r.status);
                  return (
                    <TableRow key={r.id} hover selected={selected?.id === r.id}
                      onClick={() => selectRecord(r)} sx={{ cursor: 'pointer', bgcolor: isOverdue ? '#ffebee30' : undefined }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {r.employee?.firstName} {r.employee?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{r.employee?.position?.title} · {r.employee?.department?.name}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{r.startDate || '—'}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" color={isOverdue ? 'error.main' : undefined} fontWeight={isOverdue ? 700 : 400}>
                          {r.deadline || '—'}{isOverdue ? ' ⚠️' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                          <Typography variant="caption">{r.completedTasks}/{r.totalTasks}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={STATUS_CFG[r.status]?.label}
                          sx={{ bgcolor: STATUS_CFG[r.status]?.bg, color: STATUS_CFG[r.status]?.color, fontWeight: 600, fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell>
                        {r.accessToken && (
                          <Tooltip title={copied ? 'Copied!' : 'Copy portal link'}>
                            <IconButton size="small" onClick={e => { e.stopPropagation(); copyPortalLink(r.accessToken!); }}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {r.status === 'submitted' && (
                            <Button size="small" variant="outlined" color="success"
                              startIcon={<Verified />}
                              onClick={e => { e.stopPropagation(); setVerifyOpen(r.id); setVerifyNotes(''); }}>
                              Verify
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Detail panel */}
      {selected && (
        <Paper sx={{ width: 360, p: 2, flexShrink: 0, overflowY: 'auto', maxHeight: '90vh', position: 'sticky', top: 24 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {selected.employee?.firstName} {selected.employee?.lastName}
            </Typography>
            <IconButton size="small" onClick={() => setSelected(null)}>✕</IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {selected.employee?.position?.title} · {selected.employee?.department?.name}
          </Typography>
          <Chip size="small" label={STATUS_CFG[selected.status]?.label}
            sx={{ bgcolor: STATUS_CFG[selected.status]?.bg, color: STATUS_CFG[selected.status]?.color, mb: 1.5 }} />

          {/* Progress */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Completion</Typography>
              <Typography variant="caption" fontWeight={600}>
                {selected.completedTasks}/{selected.totalTasks} tasks
              </Typography>
            </Box>
            <LinearProgress variant="determinate"
              value={selected.totalTasks > 0 ? Math.round((selected.completedTasks / selected.totalTasks) * 100) : 0}
              sx={{ height: 8, borderRadius: 4 }} />
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Collected info summary */}
          {selected.status !== 'not_started' && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Collected Data</Typography>
              {[
                { label: 'Personal Email', value: selected.personalEmail },
                { label: 'Phone', value: selected.phoneNumber },
                { label: 'Address', value: selected.address },
                { label: 'Bank', value: selected.bankName && `${selected.bankName}${selected.bankCode ? ` (${selected.bankCode})` : ''}` },
                { label: 'Account', value: selected.bankAccount },
                { label: 'Emergency', value: selected.emergencyName && `${selected.emergencyName} (${selected.emergencyRelationship})` },
                { label: 'Laptop OS', value: selected.laptopOs },
                { label: 'Dietary', value: selected.dietaryRequirements },
              ].filter(i => i.value).map(i => (
                <Box key={i.label} sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>{i.label}:</Typography>
                  <Typography variant="caption">{i.value}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Task checklist */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Checklist</Typography>
          <List dense disablePadding>
            {(selected.tasks ?? []).map((task: any) => (
              <ListItem key={task.id} disablePadding sx={{ mb: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox size="small" edge="start" checked={task.isCompleted}
                    onChange={() => toggleTask(selected.id, task.id)} sx={{ p: 0 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ textDecoration: task.isCompleted ? 'line-through' : 'none', opacity: task.isCompleted ? 0.6 : 1 }}>
                      {TASK_TYPE_ICON[task.taskType]} {task.title}
                      {task.isRequired && <Chip size="small" label="required" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14, bgcolor: '#ffebee', color: '#b71c1c' }} />}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Portal link */}
          {selected.accessToken && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Portal Link</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy link to clipboard'}>
                  <Button size="small" startIcon={<ContentCopy />} variant="outlined"
                    onClick={() => copyPortalLink(selected.accessToken!)}>
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                </Tooltip>
                <Tooltip title="Open portal in new tab">
                  <IconButton size="small"
                    onClick={() => window.open(`/pre-boarding/portal/${selected.accessToken}`, '_blank')}>
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Share this with the new hire. No login required.
              </Typography>
            </>
          )}

          {selected.hrNotes && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700}>HR Notes</Typography>
              <Typography variant="body2" color="text.secondary">{selected.hrNotes}</Typography>
            </>
          )}

          {selected.status === 'submitted' && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Button fullWidth variant="contained" color="success" startIcon={<Verified />}
                onClick={() => { setVerifyOpen(selected.id); setVerifyNotes(''); }}>
                Verify Submission
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Hire Pre-Boarding</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select fullWidth label="Employee" value={createForm.employeeId}
            onChange={e => setCreateForm(p => ({ ...p, employeeId: e.target.value }))} required>
            {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName} — {e.department?.name}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }}
              value={createForm.startDate} onChange={e => setCreateForm(p => ({ ...p, startDate: e.target.value }))} />
            <TextField fullWidth label="Deadline (must complete by)" type="date" InputLabelProps={{ shrink: true }}
              value={createForm.deadline} onChange={e => setCreateForm(p => ({ ...p, deadline: e.target.value }))} />
          </Box>
          <TextField fullWidth label="Welcome Message (optional)" multiline rows={3}
            value={createForm.welcomeMessage} onChange={e => setCreateForm(p => ({ ...p, welcomeMessage: e.target.value }))}
            placeholder="Welcome to Humanity Protocol! We're so excited to have you join us…" />
          <Typography variant="caption" color="text.secondary">
            A 9-task checklist will be created automatically. A unique portal link will be generated to share with the new hire — no HRIS account needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving || !createForm.employeeId}
            sx={{ bgcolor: '#C08A2E' }}>
            {saving ? 'Creating…' : 'Create & Generate Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify dialog */}
      <Dialog open={!!verifyOpen} onClose={() => setVerifyOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Pre-Boarding Submission</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Confirm you have reviewed all submitted information and documents.
          </Typography>
          <TextField fullWidth label="HR Notes (optional)" multiline rows={3} sx={{ mt: 1 }}
            value={verifyNotes} onChange={e => setVerifyNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyOpen(null)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<Verified />} onClick={verify}>Mark Verified</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
